<template>
  <!-- I've had to add 'touchless' property here as we had an issue where pinch zooming on the orders page and zooming
  out again lost the menu. The property is poorly documented so i'm not sure exactly what we are losing. -->
  <v-navigation-drawer
    class="bm-navigation-drawer"
    :clipped="screenSize.smAndUp"
    :value="value"
    app
    :width="250"
    :mobile-breakpoint="1340"
    touchless
    @input="handleChange($event)"
  >
    <v-list dense expand>
      <template v-for="menuItem in menuItems">
        <v-list-item
          v-if="
            !menuItem.subMenus ||
            menuItem.subMenus.filter((m) => m.visible).length === 0
          "
          :key="menuItem.translationKey"
          :to="menuItem.url"
          :data-qa="menuItem.translationKey"
          ripple="ripple"
          style="text-decoration: none"
          :target="
            menuItem.routeMetadata.target
              ? menuItem.routeMetadata.target
              : '_self'
          "
        >
          <v-list-item-icon v-if="menuItem.icon" class="mr-4">
            <bm-icon :icon="menuItem.icon" />
          </v-list-item-icon>
          <component
            :is="menuItem.menuComponent"
            v-if="menuItem.menuComponent"
            :menu-item="menuItem"
          />
          <v-list-item-title v-else>
            {{ menuItem.displayText() }}
          </v-list-item-title>
          <v-list-item-icon v-if="menuItem.routeMetadata.target === '_blank'">
            <bm-icon icon="OpenInNew" />
          </v-list-item-icon>
        </v-list-item>

        <!-- Childbearing menu items -->
        <v-list-group
          v-else
          :key="menuItem.translationKey"
          v-model="menuItem.isActive"
          :data-qa="menuItem.translationKey"
        >
          <template #activator>
            <v-list-item-icon v-if="menuItem.icon" class="mr-4">
              <bm-icon :icon="menuItem.icon" />
            </v-list-item-icon>
            <v-list-item-title>{{ menuItem.displayText() }}</v-list-item-title>
          </template>

          <template
            v-for="subMenuItem in menuItem.subMenus.filter(
              showMenuItemForCurrentUser
            )"
          >
            <v-list-item
              v-if="
                !subMenuItem.subMenus ||
                subMenuItem.subMenus.every((m) => !m.visible)
              "
              :key="subMenuItem.translationKey"
              :to="subMenuItem.url"
              :data-qa="subMenuItem.translationKey"
              ripple="ripple"
            >
              <v-list-item-content
                class="ml-4"
                no-action
                sub-group
                value="true"
              >
                <v-list-item-title>{{
                  subMenuItem.displayText()
                }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- 2nd Level Childbearing menu items -->
            <v-list-group
              v-else
              :key="subMenuItem.translationKey"
              v-model="subMenuItem.isActive"
              :data-qa="subMenuItem.translationKey"
              class="ml-4"
            >
              <template #activator>
                <v-list-item-icon v-if="subMenuItem.icon" class="mr-4">
                  <bm-icon :icon="subMenuItem.icon" />
                </v-list-item-icon>
                <v-list-item-title>{{
                  subMenuItem.displayText()
                }}</v-list-item-title>
              </template>

              <template
                v-for="subSubMenuItem in subMenuItem.subMenus.filter(
                  showMenuItemForCurrentUser
                )"
              >
                <v-list-item
                  v-if="
                    !subSubMenuItem.subMenus ||
                    subSubMenuItem.subMenus.every((m) => !m.visible)
                  "
                  :key="subSubMenuItem.translationKey"
                  :to="subSubMenuItem.url"
                  :data-qa="subSubMenuItem.translationKey"
                  ripple="ripple"
                >
                  <v-list-item-content
                    class="ml-4"
                    no-action
                    sub-group
                    value="true"
                  >
                    <v-list-item-title>{{
                      subSubMenuItem.displayText()
                    }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- Third Level -->
                <v-list-group
                  v-else
                  :key="subSubMenuItem.translationKey"
                  v-model="subSubMenuItem.isActive"
                  :data-qa="subSubMenuItem.translationKey"
                  class="ml-4"
                >
                  <template #activator>
                    <v-list-item-title>{{
                      subSubMenuItem.displayText()
                    }}</v-list-item-title>
                  </template>
                  <v-list-item
                    v-for="subItem in subSubMenuItem.subMenus.filter(
                      showMenuItemForCurrentUser
                    )"
                    :key="subItem.translationKey"
                    :to="subItem.url"
                    :data-qa="subItem.translationKey"
                    ripple="ripple"
                  >
                    <v-list-item-content
                      class="ml-4"
                      no-action
                      sub-group
                      value="true"
                    >
                      <v-list-item-title>{{
                        subItem.displayText()
                      }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-group>
              </template>
            </v-list-group>
          </template>
        </v-list-group>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import {
  buildFavouritesMenuContents,
  findFavouritesMenuHolder,
} from "@/app/ui/navigation/mainMenu";
import {
  buildMenuItems,
  showMenuItemForCurrentUser,
} from "@/app/ui/navigation/menuItems";
import { computed } from "vue";
import { screenSize } from "@/vue23switching/screenSize";

interface BmNavigationDrawerProps {
  value?: boolean;
}

defineProps<BmNavigationDrawerProps>();

interface BmNavigationDrawerEvents {
  (event: "update:value", value: boolean): void;
}

const emit = defineEmits<BmNavigationDrawerEvents>();

const menuItems = computed(() => {
  // Because Favourites are references to other menu items they don't occur natively in the generated menu
  // So get all the native items, then fill in the contents of the favourites node.
  const items = buildMenuItems();
  const favouritesNode = findFavouritesMenuHolder(items);
  if (favouritesNode) {
    favouritesNode.subMenus = buildFavouritesMenuContents(items);
  }
  return items.filter((mi) => showMenuItemForCurrentUser(mi));
});

const handleChange = (evt: boolean) => {
  emit("update:value", evt);
};
</script>

<style lang="sass" scoped>
.bm-navigation-drawer
  // Ensure that if phone browser controls cover the bottom of the menu,
  // the content is scrollable - Vuetify defaults height to 100vh, and some
  // browsers don't take controls into viewport height, so users may not be able
  // to scroll to the bottom menu item.
  height: 100% !important
</style>
